import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Import your authentication hook
import useAuth from '../hooks/useAuth';

import './CSS/Syncx.css';
import { newRoom, joinRoom } from '../sync-x/client/js/client.js';
import { eventTitles } from '../sync-x/client/js/eventInformations';

function createVideoElement(setShowLogos) {
  const videoElem = document.createElement('video');
  videoElem.id = 'syncx-player';
  videoElem.className = 'video-js vjs-default-skin vjs-fluid intoreality-player';
  videoElem.setAttribute('controls', '');
  videoElem.setAttribute('crossorigin', 'anonymous');
  videoElem.setAttribute('playsinline', '');
  videoElem.setAttribute('preload', 'auto');
  videoElem.setAttribute('data-setup', '{"fluid": true}');

  document.body.appendChild(videoElem);
  setShowLogos(false); // Hide logos when the player is created
}

function Syncx() {
  const [selectedEventIndex, setSelectedEventIndex] = useState('0');
  const [sessionId, setSessionId] = useState('');
  const [inRoom, setInRoom] = useState(false);
  const [showLogos, setShowLogos] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  // Grab user data and loading state from your auth hook
  const { userData, authLoading } = useAuth(false);

  useEffect(() => {
    // If still loading auth state, do nothing yet
    if (authLoading) return;

    // If no user data, redirect to login (account) page
    if (!userData) {
      navigate('/account', { state: { from: location } });
      return;
    }

    // Continue with Sync-X logic:
    const pathParts = window.location.pathname.split('/');
    const lastPart = pathParts[pathParts.length - 1];

    if (lastPart && lastPart.toLowerCase() !== 'syncx') {
      setSessionId(lastPart);

      const eventSelect = document.getElementById('inputEvent');
      const idInput = document.getElementById('sessionId');
      if (eventSelect && idInput) {
        eventSelect.value = selectedEventIndex;
        idInput.value = lastPart;
      }

      createVideoElement(setShowLogos);
      joinRoom(lastPart);
      setInRoom(true);
    }
  }, [authLoading, userData, navigate, location, selectedEventIndex]);

  const handleCreate = () => {
    const eventSelect = document.getElementById('inputEvent');
    const idInput = document.getElementById('sessionId');
    if (eventSelect && idInput) {
      eventSelect.value = selectedEventIndex;
      idInput.value = sessionId;
    }

    createVideoElement(setShowLogos);
    newRoom();
    window.history.pushState({}, '', `/Syncx/${sessionId}`);
    setInRoom(true);
  };

  const handleJoin = () => {
    const eventSelect = document.getElementById('inputEvent');
    const idInput = document.getElementById('sessionId');
    if (eventSelect && idInput) {
      eventSelect.value = selectedEventIndex;
      idInput.value = sessionId;
    }

    createVideoElement(setShowLogos);
    joinRoom();
    window.history.pushState({}, '', `/Syncx/${sessionId}`);
    setInRoom(true);
  };
  
  // New function to quit and go back to home page
  const handleQuit = () => {
    navigate('/');
  };

  // If auth is still loading, or we just triggered a redirect, we can render null or a loader
  if (authLoading) {
    return null; // or return a loader if you want
  }

  // If the user is not logged in, the effect above should redirect before rendering
  if (!userData) {
    return null;
  }

  return (
    <div>
      {!inRoom && (
        <div className="syncx-overlay">
          <div className="syncx-container">
            {showLogos && (
              <div className="syncx-logo-container">
                <img
                  src="https://dzsyhtfpcz9op.cloudfront.net/staticResources/logo_intoreality.tv.png"
                  alt="Intoreality Logo"
                  className="syncx-logo intoreality-logo"
                />
                <img
                  src="https://cerca.cat/wp-content/uploads/2022/07/i2cat-logo.png"
                  alt="i2Cat Logo"
                  className="syncx-logo"
                />
              </div>
            )}

            <h2 className="syncx-title">Sync-X Demo</h2>

            {/* Drop-down menu to choose the event */}
            <select
              className="syncx-input"
              value={selectedEventIndex}
              onChange={(e) => setSelectedEventIndex(e.target.value)}
              id="inputEvent"
            >
              {eventTitles.map((title, index) => (
                <option key={index} value={index}>
                  {title}
                </option>
              ))}
            </select>

            <input
              type="text"
              className="syncx-input"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              placeholder="Session ID"
              id="inputSessionId"
            />

            <div className="syncx-buttons">
              <button className="syncx-button" onClick={handleCreate}>
                Create Room
              </button>
              <button className="syncx-button" onClick={handleJoin}>
                Join Room
              </button>
              {/* New Quit button */}
              <button className="syncx-button" onClick={handleQuit}>
                Quit
              </button>
            </div>
          </div>
        </div>
      )}

      {inRoom && (
        // Hidden inputs for legacy client logic
        <div style={{ display: 'none' }}>
          <input type="hidden" id="vidUrl" />
          <input type="hidden" id="sessionId" />
        </div>
      )}
    </div>
  );
}

export default Syncx;
